/*
 * Copyright (c) CIRROM 2025.
 */

import { useState, useEffect } from "react";
import {
  ContactsTimeline,
  getDataProjectSystemDetail,
  getIntegratedSystemDetail,
  getPendingChangesSummary,
  getScoreStats,
  IntegratedSystem,
  IntegratedSystemDetail,
  PendingChangesSummary,
} from "../../../api/integratedsystems";
import {
  ChangeEntityMetric,
  getChangeEntityMetrics,
  getWrittenChangesMetricsRolling,
} from "../../../api/suggestedchanges";
import {
  getStatsForSystemOrProject,
  StatsForSystemOrProject,
} from "../../../api/dashboards";

export const useSystemStats = (
  system: IntegratedSystem,
  dataProjectId: number
) => {
  const [systemStats, setSystemStats] = useState<StatsForSystemOrProject>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useEffect(() => {
    const abortController = new AbortController();
    if (system || dataProjectId) {
      setIsLoading(true);
      getStatsForSystemOrProject(system && system.id, dataProjectId).then(
        (results) => {
          if (!abortController.signal.aborted) {
            setSystemStats(results);
            setIsLoading(false);
          }
        }
      );
    }

    return () => {
      abortController.abort();
    };
  }, [system, dataProjectId]);
  return { systemStats, isLoading };
};

export const useSystemDetails = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [systemDetails, setSystemDetails] = useState<IntegratedSystemDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject || dataProjectId) {
      setIsLoading(true);
      const request = dataProjectId
        ? getDataProjectSystemDetail(dataProjectId)
        : getIntegratedSystemDetail(systemOrProject.id);
      request.then((response) => {
        if (!abortController.signal.aborted) {
          setSystemDetails(response);
          setIsLoading(false);
        }
      });
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { systemDetails, isLoading };
};

export const usePendingChanges = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [pendingChanges, setPendingChanges] = useState<PendingChangesSummary>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject) {
      setIsLoading(true);
      getPendingChangesSummary(dataProjectId, systemOrProject?.id, false).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setPendingChanges(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { pendingChanges, isLoading };
};

export const useScoreStats = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [scoreStats, setScoreStats] = useState<ContactsTimeline[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject || dataProjectId) {
      setIsLoading(true);
      getScoreStats(systemOrProject?.id, dataProjectId).then((response) => {
        if (!abortController.signal.aborted) {
          setScoreStats(response);
          setIsLoading(false);
        }
      });
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { scoreStats, isLoading };
};

export const useGetWrittenChangesStats = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [writtenChangesStats, setWrittenChangesStats] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject || dataProjectId) {
      setIsLoading(true);
      getWrittenChangesMetricsRolling(systemOrProject?.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setWrittenChangesStats(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { writtenChangesStats, isLoading };
};

export const useGetChangeEntityMetrics = (
  systemOrProject: IntegratedSystem,
  dataProjectId?: number
) => {
  const [changeEntityMetrics, setChangeEntityMetrics] =
    useState<ChangeEntityMetric[]>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    if (systemOrProject || dataProjectId) {
      setIsLoading(true);
      getChangeEntityMetrics(systemOrProject?.id, dataProjectId).then(
        (response) => {
          if (!abortController.signal.aborted) {
            setChangeEntityMetrics(response);
            setIsLoading(false);
          }
        }
      );
    }
    return () => {
      abortController.abort();
    };
  }, [systemOrProject, dataProjectId]);

  return { changeEntityMetrics, isLoading };
};
